import React, { useEffect, useState } from 'react';
import { theme } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import ApiService from 'utils/ApiCalls';
import { DependencyTree } from '../DependencyTree';
import Helpers from 'utils/Helpers';
import { renderEvdNode } from './renderNode';
import * as Styled from '../../Layouts/Layout.styled';
import * as LocalStyled from 'pages/Reporting/ControlReport/ControlReport.styled';
import { bruEvidenceLevels } from 'constants/bruEvidenceLevels';
import { useTranslation } from 'react-i18next';

const { useToken } = theme;

const BRUEvidencesReport = () => {
  const { token } = useToken();
  const { scheme } = useSelector((state: RootState) => state.global);
  const { t } = useTranslation();
  const { orgContext, lang } = useSelector((state: RootState) => ({
    orgContext: state.global.hierarchicalLevelsScope,
    lang: state.global.lang
  }));

  const [reportData, setReportData] = useState<any[]>([]);
  // report api

  useEffect(() => {
    const filterParams = Helpers.toApiFilters(orgContext);
    const normalizedFilters = Helpers.normalizeFilters(
      Helpers.toApiFilters(orgContext)
    );

    const fetchReport = async () => {
      try {
        if (!orgContext || Object.keys(orgContext).length === 0) return;
        const hasFilters = Object.keys(filterParams).length > 0;
        if (!hasFilters) return;
        const response = await ApiService.getBruEvidences(
          lang,
          normalizedFilters
        );

        const responseData = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        const formattedData = responseData.flatMap(
          (item: any, index: number) => {
            const evidences = Object.values(item.EVIDENCES || {});

            if (!evidences.length) {
              return [
                {
                  ...item,
                  key: item.id || index,
                  evd_id: null,
                  evd_description: null
                }
              ];
            }

            return evidences.map((e: any, i: number) => ({
              ...item,
              key: `${item.id}-${i}`,
              evd_id: e.id ?? i,
              evd_description: e.description,
              evd_approved: e.approved,
              evd_approver_name: e.approver_name,
              evd_reviewed: e.reviewed,
              evd_reviewer_name: e.reviewer_name,
              evd_valid_from: e.valid_from,
              evd_valid_to: e.valid_to,
              evd_sol_desc: e.sol_desc,
              evd_created_by: e.created_by,
              evd_created_on: e.created_on,
              evd_unused: e.evd_unused
            }));
          }
        );
        setReportData(formattedData);
      } catch (error) {
        console.error(error);
        setReportData([]);
      }
    };

    fetchReport();
  }, []);

  return (
    <Styled.StyledLayout token={token}>
      <TitleBar
        title={t('BRU_EVIDENCES')}
        goBackButtons={<GoBackButton to='/dependencies' text={t('GO_BACK')} />}
      />
      <Styled.HorizontalLayoutMain theme={scheme}>
        <Styled.Container>
          <Styled.ContainerDependency token={token}>
            <Styled.CardItemWrapper>
              {reportData.length === 0 ? (
                <LocalStyled.EmptyState>
                  {t('common.noData')}
                </LocalStyled.EmptyState>
              ) : (
                <DependencyTree
                  data={reportData}
                  levels={bruEvidenceLevels}
                  renderNode={renderEvdNode}
                />
              )}
            </Styled.CardItemWrapper>
          </Styled.ContainerDependency>
        </Styled.Container>
      </Styled.HorizontalLayoutMain>
    </Styled.StyledLayout>
  );
};

export default BRUEvidencesReport;
